import { AlgoliaStoreData } from '@jbhi-fi/apis/src/algolia/types/AlgoliaStoreData';
import { algoliasearch } from 'algoliasearch';
import { baseConfig, jbConfig } from '../../config/algolia';
import { AlgoliaLocationData, AlgoliaLocationRequest, AlgoliaLocationResponse, AlgoliaStoresRequest } from './types';
import { parseAlgoliaLocationData, parseAlgoliaStoresData } from './utils';

const algoliaClient = algoliasearch(baseConfig.app_id ?? '', baseConfig.search_api_key ?? '');

export abstract class AlgoliaApiClient {
  static getLocations = async ({
    body,
    approximateLocation,
    aroundLatLngViaIP = false,
    hitsPerPage = 5,
    filters = 'hasCoordinates:true',
  }: AlgoliaLocationRequest): Promise<Array<AlgoliaLocationResponse>> => {
    const { hits } = await algoliaClient.searchSingleIndex<AlgoliaLocationData>({
      indexName: jbConfig.algliaStorePostcodeIndex ?? '',
      searchParams: {
        query: body,
        aroundLatLng: approximateLocation && `${approximateLocation.latitude}, ${approximateLocation.longitude}`,
        aroundLatLngViaIP,
        hitsPerPage,
        filters,
      },
    });

    if (!hits.length) {
      throw { message: 'No available stores found for the given location.' };
    }
    return parseAlgoliaLocationData(hits);
  };

  static getStores = async ({
    query,
    approximateLocation,
    aroundRadius = 200 * 1000,
    hitsPerPage = 5,
    filters = `displayOnWeb:${jbConfig.algoliaStoreShowOnWeb}`,
  }: AlgoliaStoresRequest): Promise<Array<AlgoliaStoreData>> => {
    const { hits } = await algoliaClient.searchSingleIndex<AlgoliaStoreData>({
      indexName: jbConfig.algliaStoreLocatorIndex ?? '',
      searchParams: {
        query,
        aroundLatLng:
          approximateLocation?.latitude && approximateLocation?.longitude
            ? `${approximateLocation.latitude}, ${approximateLocation.longitude}`
            : undefined,
        aroundRadius,
        hitsPerPage,
        filters,
      },
    });
    return parseAlgoliaStoresData(hits, approximateLocation);
  };

  static getStore = async (storeId: string): Promise<AlgoliaStoreData> => {
    const storeData = (await algoliaClient.getObject({
      indexName: jbConfig.algliaStoreLocatorIndex ?? '',
      objectID: storeId,
    })) as unknown as AlgoliaStoreData;

    return parseAlgoliaStoresData([storeData])[0];
  };

  static getStoresByIds = async (storeIds: Array<string>): Promise<Array<AlgoliaStoreData>> => {
    if (storeIds.length === 0) {
      return [];
    }
    const response = await algoliaClient.getObjects<AlgoliaStoreData>({
      requests: storeIds.map(storeId => ({
        indexName: jbConfig.algliaStoreLocatorIndex ?? '',
        objectID: storeId,
      })),
    });
    return parseAlgoliaStoresData(response.results.filter(result => result?.isActive));
  };
}
